<template>
  <div :class="['permission-cell', { 'is-changed': changed }]">
    <div class="permission-cell-inner">
      <div class="permission-select-shell">
        <div
          v-if="mode === 'view'"
          class="permission-view-tag"
          :style="selectCssVars"
        >
          {{ currentLabel }}
        </div>
        <el-dropdown
          v-else
          class="permission-dropdown"
          trigger="click"
          @command="handleCommand"
        >
          <div class="permission-dropdown-trigger" :style="selectCssVars">
            <span class="permission-dropdown-label">{{ currentLabel }}</span>
            <span class="permission-dropdown-caret">▼</span>
          </div>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="option in options"
                :key="option.value"
                :command="option.value"
              >
                {{ option.label }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  type PermissionValue = 'full' | 'edit' | 'view' | 'limited' | 'none';
  type PermissionOption = {
    value: PermissionValue;
    label: string;
  };

  const props = withDefaults(
    defineProps<{
      modelValue: PermissionValue;
      status: PermissionValue;
      changed: boolean;
      options: PermissionOption[];
      mode?: 'edit' | 'view';
    }>(),
    {
      mode: 'edit'
    }
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', value: PermissionValue): void;
  }>();

  const handleCommand = (value: PermissionValue) => {
    emit('update:modelValue', value);
  };

  const statusStyleMap: Record<
    PermissionValue,
    {
      '--permission-bg': string;
      '--permission-border': string;
      '--permission-text': string;
    }
  > = {
    full: {
      '--permission-bg': '#e8f5e9',
      '--permission-border': '#8bc34a',
      '--permission-text': '#4e6b33'
    },
    edit: {
      '--permission-bg': '#edf5ff',
      '--permission-border': '#90caf9',
      '--permission-text': '#4b647e'
    },
    view: {
      '--permission-bg': '#fff8e1',
      '--permission-border': '#f2c94c',
      '--permission-text': '#7a5d00'
    },
    limited: {
      '--permission-bg': '#eef7f5',
      '--permission-border': '#9fd3c7',
      '--permission-text': '#48635e'
    },
    none: {
      '--permission-bg': '#f7f7f7',
      '--permission-border': '#dcdfe6',
      '--permission-text': '#606266'
    }
  };

  const selectCssVars = computed(() => statusStyleMap[props.status]);
  const currentLabel = computed(() => {
    return (
      props.options.find((option) => option.value === props.modelValue)
        ?.label ?? ''
    );
  });
</script>

<style scoped lang="scss">
  .permission-cell {
    width: 100%;
    min-height: 40px;
    height: 100%;
    display: flex;
    padding: 0;
    background: transparent;
    overflow: hidden;
    box-sizing: border-box;
  }

  .permission-cell.is-changed {
    background: #fff3bf;
  }

  .permission-cell-inner {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 40px;
    height: 100%;
    padding: 0;
    box-sizing: border-box;
  }

  .permission-select-shell {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    margin: 4px;
    box-sizing: border-box;
  }

  .permission-dropdown {
    width: 100%;
  }

  .permission-view-tag,
  .permission-dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 28px;
    border-radius: 4px;
    border: 1px solid var(--permission-border, #dcdfe6);
    background: var(--permission-bg, #ffffff);
    color: var(--permission-text, #606266);
    padding: 1px 8px;
    box-sizing: border-box;
    cursor: pointer;
  }

  .permission-view-tag {
    cursor: default;
    justify-content: center;
    font-size: 12px;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .permission-dropdown-label {
    color: var(--permission-text, #606266);
    font-size: 12px;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .permission-dropdown-caret {
    color: var(--permission-text, #909399);
    font-size: 12px;
    margin-left: 8px;
    flex-shrink: 0;
  }

  .permission-dropdown-trigger:hover {
    filter: brightness(0.98);
  }
</style>
